const nullRouteSegment = '__none__';

type SyntheticJobId = {
  pipelineId: string | null;
  stageName: string | null;
  jobName: string;
};

type JobRunPathInput = SyntheticJobId & {
  jobRunId: string;
};

const encodeRouteSegment = (value: string | null): string =>
  encodeURIComponent(value ?? nullRouteSegment);

const decodeRouteSegment = (value: string | undefined): string | null => {
  if (value === undefined) {
    return null;
  }

  const decodedValue = decodeURIComponent(value);
  return decodedValue === nullRouteSegment ? null : decodedValue;
};

export const buildSyntheticJobId = (params: SyntheticJobId): string =>
  JSON.stringify([params.pipelineId, params.stageName, params.jobName]);

export const parseSyntheticJobId = (value: string): SyntheticJobId | null => {
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!Array.isArray(parsed) || parsed.length !== 3) {
      return null;
    }

    const [pipelineId, stageName, jobName] = parsed;
    if (pipelineId !== null && typeof pipelineId !== 'string') {
      return null;
    }
    if (stageName !== null && typeof stageName !== 'string') {
      return null;
    }
    if (typeof jobName !== 'string' || jobName.trim().length === 0) {
      return null;
    }

    return {
      pipelineId,
      stageName,
      jobName,
    };
  } catch {
    return null;
  }
};

export const buildSyntheticJobIdFromRoute = (params: {
  jobSlug?: string;
  pipelineId?: string;
  stageName?: string;
  jobName?: string;
}): string | null => {
  const jobSlug = decodeRouteSegment(params.jobSlug);
  if (jobSlug && parseSyntheticJobId(jobSlug)) {
    return jobSlug;
  }

  const pipelineId = decodeRouteSegment(params.pipelineId);
  const stageName = decodeRouteSegment(params.stageName);
  const jobName = decodeRouteSegment(params.jobName);
  if (!jobName || jobName.trim().length === 0) {
    return null;
  }

  return buildSyntheticJobId({
    pipelineId,
    stageName,
    jobName,
  });
};

const buildStructuredJobPath = (params: SyntheticJobId): string =>
  `/jobs/${encodeRouteSegment(buildSyntheticJobId(params))}`;

export const buildJobDetailPath = (jobIdOrParams: string | SyntheticJobId): string => {
  if (typeof jobIdOrParams !== 'string') {
    return buildStructuredJobPath(jobIdOrParams);
  }

  const parsed = parseSyntheticJobId(jobIdOrParams);
  if (!parsed) {
    return `/jobs/legacy/${encodeURIComponent(jobIdOrParams)}`;
  }

  return buildStructuredJobPath(parsed);
};

export const buildJobRunDetailPath = (jobRunIdOrParams: string | JobRunPathInput): string => {
  if (typeof jobRunIdOrParams === 'string') {
    return `/jobs/runs/${encodeURIComponent(jobRunIdOrParams)}`;
  }

  return `${buildStructuredJobPath(jobRunIdOrParams)}/runs/${encodeURIComponent(jobRunIdOrParams.jobRunId)}`;
};
